import {
  POLICY_STATUS_BY_ID,
  POLICY_STATUS_ID_BY_KEY,
  POLICY_STATUS_ORDER,
  resolveStatusKeyFromName,
} from "./agentRequestsApi.js";

export function parseDateOnly(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s.slice(0, 10));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function toDateInputValue(date) {
  const d = parseDateOnly(date);
  if (!d) return "";
  return d.toISOString().slice(0, 10);
}

export function reportRangeEndDate() {
  const end = new Date();
  return new Date(end.getFullYear(), end.getMonth(), end.getDate());
}

export function defaultReportDateRange() {
  const end = reportRangeEndDate();
  const start = new Date(end.getFullYear(), end.getMonth() - 2, 1);
  return [start, end];
}

/** Preset periods for the report dashboard filter. */
export const REPORT_PERIOD_PRESET_IDS = ["month1", "quarter3", "halfyear", "year1"];

export function getReportPeriodPresets() {
  const end = reportRangeEndDate();
  return [
    {
      id: "month1",
      getRange: () => {
        const start = new Date(end);
        start.setMonth(start.getMonth() - 1);
        return [start, new Date(end)];
      },
    },
    {
      id: "quarter3",
      getRange: defaultReportDateRange,
    },
    {
      id: "halfyear",
      getRange: () => [new Date(end.getFullYear(), end.getMonth() - 5, 1), new Date(end)],
    },
    {
      id: "year1",
      getRange: () => {
        const start = new Date(end);
        start.setFullYear(start.getFullYear() - 1);
        return [start, new Date(end)];
      },
    },
  ];
}

export function getReportPeriodPresetById(id) {
  return getReportPeriodPresets().find((p) => p.id === id) || null;
}

export function findMatchingReportPeriodPreset(dateFrom, dateTo) {
  for (const preset of getReportPeriodPresets()) {
    const [start, end] = preset.getRange();
    if (toDateInputValue(start) === dateFrom && toDateInputValue(end) === dateTo) {
      return preset.id;
    }
  }
  return null;
}

export function normalizeFilterPeriodDates(dateFrom, dateTo, getDefaultRange) {
  const fallback = getDefaultRange();
  const defFrom = toDateInputValue(fallback[0]);
  const defTo = toDateInputValue(fallback[1]);
  let from = toDateInputValue(dateFrom);
  let to = toDateInputValue(dateTo);
  if (!from && !to) {
    return { dateFrom: defFrom, dateTo: defTo };
  }
  if (!from) from = defFrom;
  if (!to) to = defTo;
  if (from > to) {
    const swap = from;
    from = to;
    to = swap;
  }
  return { dateFrom: from, dateTo: to };
}

/** Policies list — default last 10 calendar days (inclusive). */
export function defaultPoliciesDateRange() {
  const end = reportRangeEndDate();
  const start = new Date(end);
  start.setDate(start.getDate() - 9);
  return [start, end];
}

export function filterRecordsByDateRange(records, dateFrom, dateTo) {
  const from = parseDateOnly(dateFrom);
  const to = parseDateOnly(dateTo);
  if (!from && !to) return [...records];

  const toEnd = to ? new Date(to) : null;
  if (toEnd) {
    toEnd.setHours(23, 59, 59, 999);
  }

  return records.filter((row) => {
    const sale = parseDateOnly(row.saleDate ?? row.createdDate ?? row.startDate);
    if (!sale) return false;
    if (from && sale < from) return false;
    if (toEnd && sale > toEnd) return false;
    return true;
  });
}

function pickInsuredKey(row) {
  const pin = row.insuredPin ?? row.pin ?? row.insuredClientPin ?? row.clientPin;
  if (pin != null && String(pin).trim() !== "") {
    return `pin:${String(pin).trim()}`;
  }
  const id = row.insuredId ?? row.clientId ?? row.personId;
  if (id != null && String(id).trim() !== "") {
    return `id:${String(id).trim()}`;
  }
  const name = row.insuredName ?? row.insuredClient ?? row.clientName;
  if (name != null && String(name).trim() !== "") {
    return `name:${String(name).trim().toLowerCase()}`;
  }
  return null;
}

function pickProductKey(row) {
  const id = row.productId ?? row.prodId;
  if (id != null && String(id).trim() !== "") {
    return `id:${String(id).trim()}`;
  }
  const name = row.productName ?? row.prodName;
  if (name != null && String(name).trim() !== "") {
    return `name:${String(name).trim()}`;
  }
  return null;
}

export function computeReportSummary(records) {
  let premiumSum = 0;
  const insuredKeys = new Set();
  const productKeys = new Set();

  for (const row of records) {
    premiumSum += Number(row.premium ?? row.premiumAmount ?? 0) || 0;
    const insuredKey = pickInsuredKey(row);
    if (insuredKey) insuredKeys.add(insuredKey);
    const productKey = pickProductKey(row);
    if (productKey) productKeys.add(productKey);
  }

  return {
    salesCount: records.length,
    premiumSum,
    insuredCount: insuredKeys.size,
    productsUsedCount: productKeys.size,
  };
}

export function computeByProduct(records) {
  const map = new Map();
  for (const row of records) {
    const name = String(row.productName || row.prodName || "—").trim() || "—";
    if (!map.has(name)) {
      map.set(name, { productName: name, salesCount: 0, premiumSum: 0 });
    }
    const item = map.get(name);
    item.salesCount += 1;
    item.premiumSum += Number(row.premium ?? row.premiumAmount ?? 0) || 0;
  }
  return [...map.values()].sort((a, b) => b.salesCount - a.salesCount);
}

function resolveStatusKey(row) {
  if (row?.status && POLICY_STATUS_ORDER.includes(row.status)) return row.status;
  const fromName = resolveStatusKeyFromName(row?.statusName ?? row?.policyStatusName);
  if (fromName) return fromName;
  const id = Number(row?.statusId);
  const byId = POLICY_STATUS_BY_ID[id];
  if (byId) return byId;
  return null;
}

export function computeByStatus(records, { totalDenominator = null } = {}) {
  const periodTotal = records.length;
  const denom = totalDenominator != null ? totalDenominator : periodTotal;
  const safeDenom = denom > 0 ? denom : 1;

  const counts = Object.fromEntries(POLICY_STATUS_ORDER.map((key) => [key, 0]));

  for (const row of records) {
    const key = resolveStatusKey(row);
    if (key && counts[key] != null) counts[key] += 1;
  }

  return POLICY_STATUS_ORDER.map((statusKey) => {
    const count = counts[statusKey] ?? 0;
    const statusId = POLICY_STATUS_ID_BY_KEY[statusKey];
    return {
      statusKey,
      statusId,
      count,
      periodTotal,
      shareOfTotal: Math.round((count / safeDenom) * 1000) / 10,
    };
  });
}

/** Map policy list rows to report rows (premium fallback for test). */
export function mapPoliciesToReportRows(policies) {
  return (policies || []).map((row, index) => {
    const premiumRaw = row.premium ?? row.premiumAmount ?? row.insuranceFee;
    const premium =
      premiumRaw != null && Number.isFinite(Number(premiumRaw))
        ? Number(premiumRaw)
        : 100 + (index % 9) * 37;
    return {
      id: row.policyId ?? row.reqId ?? index,
      saleDate: row.startDate ?? row.createdDate ?? row.endDate ?? "",
      productId: row.productId ?? row.prodId,
      productName: row.productName,
      insuredPin: row.insuredPin ?? row.pin ?? row.insuredClientPin,
      insuredId: row.insuredId ?? row.clientId,
      insuredName: row.insuredName ?? row.insuredClient,
      premium,
      status: row.status,
      statusId: row.statusId,
    };
  });
}
